import { useState } from "react";
import {
  useCustom,
  useOne,
  useUpdate,
  useDelete,
  useCustomMutation,
  useInvalidate,
  useTranslate,
  useGetLocale,
} from "@refinedev/core";
import {
  PlusCircleSolidIcon,
  ActionBar,
  Button,
  CheckboxField,
  Field,
  FormCard,
  SectionHeader,
  SelectField,
  StatusBadge,
  Tabs,
  type Column,
  type SelectOption,
} from "@fawkes/ui";

import { formatDateTime } from "../i18n/format";
import { AVAILABLE_ROLES, roleLabel } from "../serviceAccountRoles";
import { TokenRejectionList, type TokenRejection } from "./TokenRejectionList";
import { IndeelbareTabel } from "../components/IndeelbareTabel";

interface CredentialSummary {
  id: string;
  clientId: string;
  secretPrefix: string;
  createdAt?: string;
  lastUsedAt?: string;
  lastUsedIp?: string;
  expiresAt?: string;
  removedAt?: string;
}

type CredentialAction = "FORCE_EXPIRE" | "EXTEND" | "CLEAR_END_DATE" | "SOFT_DELETE" | "RESTORE";
type ExtendDuration = "HOURS_24" | "HOURS_72" | "WEEK_1" | "MONTH_1";
type DetailTab = "identity" | "credentials" | "allowlist" | "admin";

interface AllowedIpSummary {
  id: string;
  cidr: string;
  createdAt?: string;
}

interface ServiceAccount {
  id: string;
  name: string;
  active: boolean;
  createdAt: string;
  removedAt?: string;
  authorities: string[];
  credentials: CredentialSummary[];
  allowedIps: AllowedIpSummary[];
}

// jve-5-1 + jve-5-3b + jve-2-6 parity: Service Account detail + edit. The screen mirrors the Jmix
// rich entity-detail vocabulary (Animal detail): a JmixTabSheet tab strip splits the four sections —
// identity & roles, credentials, IP allowlist, admin — and the identity edit form uses the two-column
// field grid of the Jmix User edit view. Identity + roles edit via useUpdate (PUT); the IP allowlist
// add/remove + credential lifecycle via useCustomMutation; account soft-delete/restore round out the
// admin tab.

// Pairing epic D (D8): the account's recently refused token requests (reason + caller IP), under the
// allowlist — so "the platform gets invalid_client" becomes "203.0.113.9 is not allowed" with an
// "IP toevoegen" next to it. Renders nothing when the endpoint is unavailable.
function RefusedTokenRequests({
  id,
  onAllowIp,
  disabled,
}: {
  id: string;
  onAllowIp: (ip: string) => void;
  disabled: boolean;
}) {
  const t = useTranslate();
  const { data, isError } = useCustom<TokenRejection[]>({
    url: `service-accounts/${id}/token-rejections`,
    method: "get",
    errorNotification: false,
    queryOptions: { retry: false },
  });
  if (isError) return null;
  const rows = Array.isArray(data?.data) ? data.data : [];
  return (
    <div className="fawkes-detail-section">
      <p className="fui-field__label">{t("serviceAccountDetailView.rejections.title")}</p>
      <TokenRejectionList
        rejections={rows}
        onAllowIp={onAllowIp}
        disabled={disabled}
        empty={t("serviceAccountDetailView.rejections.none")}
      />
    </div>
  );
}

export function ServiceAccountDetail({ id, onBack }: { id: string; onBack: () => void }) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "en";
  const { data, isLoading, isError } = useOne<ServiceAccount>({ resource: "service-accounts", id });
  const { mutate, isLoading: isSaving } = useUpdate();
  const { mutate: mutateCustom, isLoading: isMutatingAllowlist } = useCustomMutation();
  const { mutate: mutateDelete } = useDelete();
  const invalidate = useInvalidate();

  const [tab, setTab] = useState<DetailTab>("identity");
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [active, setActive] = useState(true);
  const [authorities, setAuthorities] = useState<string[]>([]);
  const [newCidr, setNewCidr] = useState("");
  const [cidrError, setCidrError] = useState<string | null>(null);
  const [showRemovedCreds, setShowRemovedCreds] = useState(false);
  const [extendDuration, setExtendDuration] = useState<ExtendDuration>("HOURS_24");

  const refresh = () => invalidate({ resource: "service-accounts", invalidates: ["detail"], id });

  if (isLoading) {
    return <p className="fawkes-muted">{t("common.loading")}</p>;
  }
  if (isError || !data?.data) {
    return <p className="fawkes-error">{t("serviceAccountDetailView.loadFailed")}</p>;
  }

  const sa = data.data;

  function startEdit() {
    setName(sa.name);
    setActive(sa.active);
    setAuthorities(sa.authorities ?? []);
    setEditing(true);
  }

  function toggleRole(value: string) {
    setAuthorities((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  }

  function save() {
    mutate(
      { resource: "service-accounts", id, values: { name, active, authorities } },
      { onSuccess: () => setEditing(false) },
    );
  }

  function addCidr(value: string = newCidr) {
    const cidr = value.trim();
    if (!cidr) return;
    setCidrError(null);
    mutateCustom(
      { url: `service-accounts/${id}/allowed-ips`, method: "post", values: { cidr } },
      {
        onSuccess: () => {
          setNewCidr("");
          refresh();
        },
        onError: (err) => {
          const status = (err as { statusCode?: number })?.statusCode;
          const key =
            status === 409
              ? "serviceAccountDetailView.allowlist.validation.cidr.duplicate"
              : status === 400
                ? "serviceAccountDetailView.allowlist.validation.cidr.invalid"
                : "serviceAccountDetailView.allowlist.add.failed";
          setCidrError(t(key, { args: [cidr] }));
        },
      },
    );
  }

  function removeCidr(ipId: string) {
    mutateCustom(
      { url: `service-accounts/${id}/allowed-ips/${ipId}`, method: "delete", values: {} },
      { onSuccess: () => refresh() },
    );
  }

  function softDeleteAccount() {
    mutateDelete(
      { resource: "service-accounts", id },
      { onSuccess: () => refresh() },
    );
  }

  function restoreAccount() {
    mutateCustom(
      { url: `service-accounts/${id}/restore`, method: "post", values: {} },
      { onSuccess: () => refresh() },
    );
  }

  function credentialAction(credId: string, action: CredentialAction) {
    mutateCustom(
      {
        url: `service-accounts/${id}/credentials/${credId}`,
        method: "patch",
        values: action === "EXTEND" ? { action, duration: extendDuration } : { action },
      },
      { onSuccess: () => refresh() },
    );
  }

  function forceExpireAll() {
    mutateCustom(
      { url: `service-accounts/${id}/credentials/force-expire-all`, method: "post", values: {} },
      { onSuccess: () => refresh() },
    );
  }

  const credentialColumns: Column<CredentialSummary>[] = [
    { header: t("serviceAccountDetailView.credentials.column.clientId"), cell: (c) => c.clientId },
    { header: t("serviceAccountDetailView.credentials.column.secretPrefix"), cell: (c) => c.secretPrefix },
    { header: t("serviceAccountDetailView.credentials.column.lastUsedAt"), cell: (c) => formatDateTime(c.lastUsedAt, locale) },
    { header: t("serviceAccountDetailView.credentials.column.lastUsedIp"), cell: (c) => c.lastUsedIp ?? "—" },
    { header: t("serviceAccountDetailView.credentials.column.expiresAt"), cell: (c) => formatDateTime(c.expiresAt, locale) },
    {
      header: "",
      cell: (c) =>
        c.removedAt ? (
          <Button variant="secondary" onClick={() => credentialAction(c.id, "RESTORE")} disabled={isMutatingAllowlist}>
            {t("serviceAccountDetailView.credentials.restore")}
          </Button>
        ) : (
          <span className="fawkes-row-actions">
            <Button variant="secondary" onClick={() => credentialAction(c.id, "FORCE_EXPIRE")} disabled={isMutatingAllowlist}>
              {t("serviceAccountDetailView.credentials.forceExpire")}
            </Button>
            <Button variant="secondary" onClick={() => credentialAction(c.id, "EXTEND")} disabled={isMutatingAllowlist}>
              {t("serviceAccountDetailView.credentials.extend")}
            </Button>
            <Button variant="secondary" onClick={() => credentialAction(c.id, "CLEAR_END_DATE")} disabled={isMutatingAllowlist || !c.expiresAt}>
              {t("serviceAccountDetailView.credentials.clearEndDate")}
            </Button>
            <Button variant="secondary" onClick={() => credentialAction(c.id, "SOFT_DELETE")} disabled={isMutatingAllowlist}>
              {t("serviceAccountDetailView.credentials.softDelete")}
            </Button>
          </span>
        ),
    },
  ];

  const allCreds = sa.credentials ?? [];
  const visibleCreds = showRemovedCreds ? allCreds : allCreds.filter((c) => !c.removedAt);
  const extendOptions: SelectOption[] = [
    { value: "HOURS_24", label: t("serviceAccountDetailView.credentials.extend.choice.hours24") },
    { value: "HOURS_72", label: t("serviceAccountDetailView.credentials.extend.choice.hours72") },
    { value: "WEEK_1", label: t("serviceAccountDetailView.credentials.extend.choice.week1") },
    { value: "MONTH_1", label: t("serviceAccountDetailView.credentials.extend.choice.month1") },
  ];

  const allowlistColumns: Column<AllowedIpSummary>[] = [
    { header: t("serviceAccountDetailView.allowlist.column.cidr"), cell: (ip) => ip.cidr },
    { header: t("serviceAccountDetailView.allowlist.column.createdAt"), cell: (ip) => formatDateTime(ip.createdAt, locale) },
    {
      header: "",
      cell: (ip) => (
        <Button variant="secondary" onClick={() => removeCidr(ip.id)} disabled={isMutatingAllowlist}>
          {t("serviceAccountDetailView.allowlist.remove")}
        </Button>
      ),
    },
  ];

  const allowedIps = sa.allowedIps ?? [];
  const assignedRoles = sa.authorities ?? [];

  // Tab strip — mirrors the Jmix JmixTabSheet (Animal detail). Section titles double as tab labels.
  const tabs: { key: DetailTab; label: string }[] = [
    { key: "identity", label: t("serviceAccountDetailView.identity.title") },
    { key: "credentials", label: t("serviceAccountDetailView.credentials.title") },
    { key: "allowlist", label: t("serviceAccountDetailView.allowlist.title") },
    { key: "admin", label: t("serviceAccountDetailView.adminActions.title") },
  ];

  const onTabChange = (key: string) => setTab(key as DetailTab);

  return (
    <FormCard>
      <SectionHeader>
        {editing && tab === "identity" ? t("serviceAccountDetailView.identity.edit.title") : sa.name}
      </SectionHeader>

      {/* Entity status — shown under the title so it stays visible across tabs (Jmix surfaces the
          entity state regardless of which section is open). */}
      <div className="fawkes-detail-section">
        {sa.removedAt ? (
          <StatusBadge tone="danger">{t("serviceAccountListView.status.softDeleted")}</StatusBadge>
        ) : (
          <StatusBadge tone={sa.active ? "success" : "neutral"}>
            {sa.active
              ? t("serviceAccountListView.status.active")
              : t("serviceAccountListView.status.disabled")}
          </StatusBadge>
        )}
      </div>

      <Tabs tabs={tabs} activeKey={tab} onChange={onTabChange} />

      {/* Identity & roles. */}
      {tab === "identity" && (
        <div role="tabpanel">
          {editing ? (
            <>
              <div className="fawkes-form-grid">
                <Field
                  label={t("serviceAccountDetailView.identity.username")}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <CheckboxField
                  label={t("serviceAccountDetailView.identity.active")}
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                />
                <fieldset className="fawkes-fieldset fawkes-form-grid__full">
                  <legend className="fui-field__label">{t("serviceAccountDetailView.roles.picker.label")}</legend>
                  {AVAILABLE_ROLES.map((role) => (
                    <CheckboxField
                      key={role.value}
                      label={role.label}
                      checked={authorities.includes(role.value)}
                      onChange={() => toggleRole(role.value)}
                    />
                  ))}
                </fieldset>
              </div>
              <ActionBar>
                <Button variant="primary" onClick={save} disabled={isSaving || name.trim() === ""}>
                  {t("serviceAccountDetailView.identity.edit.save")}
                </Button>
                <Button variant="secondary" onClick={() => setEditing(false)} disabled={isSaving}>
                  {t("serviceAccountDetailView.identity.edit.cancel")}
                </Button>
              </ActionBar>
            </>
          ) : (
            <>
              <dl className="fawkes-keyvalue">
                <dt>{t("serviceAccountDetailView.identity.username")}</dt>
                <dd>{sa.name}</dd>
                <dt>{t("serviceAccountDetailView.identity.createdDate")}</dt>
                <dd>{formatDateTime(sa.createdAt, locale)}</dd>
                <dt>{t("serviceAccountDetailView.roles.title")}</dt>
                <dd>
                  {assignedRoles.length > 0 ? (
                    <span className="fawkes-chips">
                      {assignedRoles.map((r) => (
                        <StatusBadge key={r} tone="neutral">
                          {roleLabel(r)}
                        </StatusBadge>
                      ))}
                    </span>
                  ) : (
                    "—"
                  )}
                </dd>
              </dl>
              <ActionBar>
                <Button variant="primary" onClick={startEdit} disabled={!!sa.removedAt}>
                  {t("serviceAccountDetailView.identity.edit")}
                </Button>
              </ActionBar>
            </>
          )}
        </div>
      )}

      {/* Credentials — lifecycle actions (jve-5-3b-2). */}
      {tab === "credentials" && (
        <div role="tabpanel">
          <div className="fawkes-list-toolbar">
            <CheckboxField
              label={t("serviceAccountDetailView.credentials.showRemoved")}
              checked={showRemovedCreds}
              onChange={(e) => setShowRemovedCreds(e.target.checked)}
            />
            <SelectField
              label={t("serviceAccountDetailView.credentials.extend")}
              options={extendOptions}
              value={extendDuration}
              onChange={(e) => setExtendDuration(e.target.value as ExtendDuration)}
            />
            <Button variant="secondary" onClick={forceExpireAll} disabled={isMutatingAllowlist}>
              {t("serviceAccountDetailView.adminActions.forceExpire")}
            </Button>
          </div>
          <IndeelbareTabel tabelKey="service-account-detail-toegestane-ips"
            columns={credentialColumns}
            rows={visibleCreds}
            getRowKey={(c) => c.id}
            empty={t("serviceAccountDetailView.credentials.none")}
          />
        </div>
      )}

      {/* IP allowlist — add/remove (jve-5-3b). Zero-IP blocks all API traffic. */}
      {tab === "allowlist" && (
        <div role="tabpanel">
          {allowedIps.length === 0 && (
            <p className="fawkes-error">{t("serviceAccountDetailView.zeroIp.warning")}</p>
          )}
          <IndeelbareTabel tabelKey="service-account-detail-weigeringen"
            columns={allowlistColumns}
            rows={allowedIps}
            getRowKey={(ip) => ip.id}
            empty={t("serviceAccountDetailView.allowlist.none")}
          />
          <div className="fawkes-list-toolbar">
            <Field
              label={t("serviceAccountDetailView.allowlist.add.label")}
              placeholder={t("serviceAccountDetailView.allowlist.add.placeholder")}
              value={newCidr}
              onChange={(e) => setNewCidr(e.target.value)}
            />
            <Button variant="primary" onClick={() => addCidr()} disabled={isMutatingAllowlist || newCidr.trim() === ""}>
              <PlusCircleSolidIcon width={18} height={18} /> {t("serviceAccountDetailView.allowlist.add")}
            </Button>
          </div>
          {cidrError && <p className="fawkes-error">{cidrError}</p>}
          <RefusedTokenRequests id={id} onAllowIp={(ip) => addCidr(ip)} disabled={isMutatingAllowlist} />
        </div>
      )}

      {/* Admin actions (jve-5-3b-3): account soft-delete / restore. */}
      {tab === "admin" && (
        <div role="tabpanel">
          {sa.removedAt ? (
            <Button variant="secondary" onClick={restoreAccount}>
              {t("serviceAccountDetailView.adminActions.restore")}
            </Button>
          ) : (
            <Button variant="secondary" onClick={softDeleteAccount}>
              {t("serviceAccountDetailView.adminActions.softDelete")}
            </Button>
          )}
        </div>
      )}

      <ActionBar>
        <Button variant="secondary" onClick={onBack}>
          {t("common.back")}
        </Button>
      </ActionBar>
    </FormCard>
  );
}
